import { useCallback, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { Formik, Form } from 'formik';
import Button from '@mui/material/Button';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Grid
} from '@mui/material';
import { alertMessages, FuelModels } from 'src/constants';
import CloseIcon from '@mui/icons-material/Close';
import { useStoreContext } from 'src/store/context';
import { useAddAlert, useOpenCalc } from 'src/store/actions';
import { errorResponse, regex2decimals, truncateTwoDecimals } from 'src/store/common';
import { getCalcResultsCall, getWeatherRelatedNFDRSCall } from 'src/apis/calculatorCalls';
import { styled } from '@mui/material/styles';
import { getNfdrCatalog, getStations } from 'src/apis/getWeatherCalls';
import LoadingSpinner from 'src/shared/loading/loading';
import moment from 'moment';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));
const MenuProps = {
  PaperProps: {
    sx: {
      zIndex: 5000
    },
  },
};
interface CalculatorProps {
  children?: React.ReactNode;
  className?: string;
  type?: string;
  icon?: string;
  onClose?: () => void;
  keyDown?: any;
}


export const validationSchema = Yup.object().shape(
  {
    fuelModel: Yup.string().nullable()
      .required('Fuel Model is Required'),
    oneHr: Yup.number().nullable().min(1, "1-HR FM must be greater than or equal to 1")
      .max(40, "1-HR FM must be less than or equal to 40")
      .required('1-HR FM is Required')
      .typeError('1-HR FM must be a number'),
    tenHr: Yup.number().nullable().min(1, "10-HR FM must be greater than or equal to 1")
      .max(40, "10-HR FM must be less than or equal to 40")
      .required('10-HR FM is Required')
      .typeError('10-HR FM must be a number'),
    hundredHr: Yup.number().nullable().min(1, "100-HR FM must be greater than or equal to 1")
      .max(40, "100-HR FM must be less than or equal to 40")
      .required('100-HR FM is Required')
      .typeError('100-HR FM must be a number'),

    thousandHr: Yup.number().nullable().min(1, "1000-HR FM must be greater than or equal to 1")
      .max(40, "1000-HR FM must be less than or equal to 40")
      .required('1000-HR FM is Required')
      .typeError('1000-HR FM must be a number'),

    woodyFM: Yup.number().nullable().min(60, "Woody FM must be greater than or equal to 60")
      .max(300, "Woody FM must be less than or equal to 300")
      .required('Woody FM is Required')
      .typeError('Woody FM must be a number'),

    herbFM: Yup.number().nullable().min(30, "Herbaceous FM must be greater than or equal to 30")
      .max(300, "Herbaceous FM must be less than or equal to 300")
      .required('Herbaceous FM is Required')
      .typeError('Herbaceous FM  must be a number'),

    windSpeed: Yup.number().nullable().min(0, "Wind Speed must be greater than or equal to 0")
      .max(99, "Wind Speed must be less than or equal to 99")
      .required("Wind Speed is Required")
      .typeError("Wind Speed Must be a number"),

    kbdi: Yup.number().nullable().min(0, "KBDI must be greater than or equal to 0")
      .max(800, "KBDI must be less than or equal to 800")
      .required('KBDI is Required')
      .typeError('KBDI must be a number'),

    fuelTemp: Yup.number().nullable().min(0, "Fuel Temperature must be greater than or equal to 0")
      .max(150, "Fuel Temperature must be less than or equal to 150")
      .required('Fuel Temperature is Required')
      .typeError('Fuel Temperature must be a number'),

    gsi: Yup.number().nullable().min(0, "GSI must be greater than or equal to 0")
      .max(1, "GSI must be less than or equal to 1")
      .required('GSI is Required')
      .typeError('GSI must be a number'),

    temperature: Yup.number().nullable()
      .when('calcType', {
        is: (calcType: string) => calcType === 'Scenario',
        then: Yup.number().nullable().required('Temperature is Required'),
        otherwise: Yup.number().nullable().notRequired()
      })
      .min(-74, "Temperature must be greater than or equal to -74")
      .max(138, "Temperature must be less than or equal to 138"),


    sol_rad: Yup.number().nullable()
      .when('calcType', {
        is: (calcType: string) => calcType === 'Scenario',
        then: Yup.number().nullable().required('Solar Radiation is Required'),
        otherwise: Yup.number().nullable().notRequired()
      })
      .min(0, "Solar Radiation must be greater than or equal to 0")
      .max(1999, "Solar Radiation must be less than or equal to 1999"),

    relative_humidity: Yup.number().nullable()
      .when('calcType', {
        is: (calcType: string) => calcType === 'Scenario',
        then: Yup.number().nullable().required('Relative Humidity is Required'),
        otherwise: Yup.number().nullable().notRequired()
      })
      .min(1, "Relative Humidity must be greater than or equal to 1")
      .max(99, "Relative Humidity must be less than or equal to 99"),

    precip: Yup.number().nullable()
      .when('calcType', {
        is: (calcType: string) => calcType === 'Scenario',
        then: Yup.number().nullable().required('Precipitation is Required'),
        otherwise: Yup.number().nullable().notRequired()
      })
      .min(0, "Precipitation must be greater than or equal to 0")
      .max(20, "Precipitation must be less than or equal to 20"),

    snow_flag: Yup.string().nullable()
      .when('calcType', {
        is: (calcType: string) => calcType === 'Scenario',
        then: Yup.string().nullable().required('Snow Flag is Required'),
        otherwise: Yup.string().nullable().notRequired()
      }),
    station_id: Yup.string().nullable()
      .when('calcType', {
        is: (calcType: string) => calcType === 'Scenario',
        then: Yup.string().nullable().required('Station is Required'),
        otherwise: Yup.string().nullable().notRequired()
      })
  }
);

interface FormVals {
  calcType: string,
  fuelModel: string,

  oneHr: number | null,
  tenHr: number | null,
  hundredHr: number | null,
  thousandHr: number | null,
  woodyFM: number | null,
  herbFM: number | null,

  windSpeed: number | null,
  kbdi: number | null,
  fuelTemp: number | null,
  gsi: number | null,

  temperature: number | null,
  sol_rad: number | null,
  relative_humidity: number | null,
  precip: number | null,
  snow_flag: string | null,
  station_id: string | null
}

export const Calculator = (props: CalculatorProps): React.ReactElement => {
  const [, setIsDragging] = useState(false);
  const [, dispatch] = useStoreContext();
  const setOpenCalc = useOpenCalc(dispatch);
  const addAlert = useAddAlert(dispatch);
  const [outputFromCalc, setOutputFromCalc] = useState<any>({ bi: null, erc: null, sc: null, ic: null })
  const [catalogLinkedToStation, setCatalogLinkedToStation] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [time, setTime] = useState<any>(null);
  const [tz, setTz] = useState<string>('');
  const [stationsOptions, setStationsOptions] = useState<any>([])
  const [failureCount, setFailureCount] = useState<number>(0)

  const [initVals, setInitVals] = useState<FormVals>({
    calcType: 'Scenario',
    fuelModel: 'Y',
    oneHr: 3,
    tenHr: 4,
    hundredHr: 5,
    thousandHr: 6,
    woodyFM: 60,
    herbFM: 30,
    windSpeed: 5,
    kbdi: 100,
    fuelTemp: 80,
    gsi: 0.3,
    temperature: null,
    sol_rad: null,
    relative_humidity: null,
    precip: null,
    snow_flag: 'N',
    station_id: null,
  })

  const getNFDRSfromWxCallback = useCallback((stationId: number, value: any) => {
    (async () => {
      try {
        const varRequest = {
          stationId: stationId,
          fuelModel: value.fuelModel,
          dateTimeFormat: 'UTC'
        };
        const response: any = await getWeatherRelatedNFDRSCall(varRequest, addAlert);
        if (response?.data?.length > 0) {
          const responseValue = response.data[0];
          setTime(moment.parseZone(responseValue.display_hour_lst).format('MMM DD, YYYY HH:mm'))
          setInitVals({
            calcType: 'Scenario',
            fuelModel: value.fuelModel,
            oneHr: Number(responseValue.one_hr_tl_fuel_moisture),
            tenHr: Number(responseValue.ten_hr_tl_fuel_moisture),
            hundredHr: Number(responseValue.hun_hr_tl_fuel_moisture),
            thousandHr: Number(responseValue.thou_hr_tl_fuel_moisture),
            woodyFM: Number(responseValue.woody_lfi_fuel_moisture),
            herbFM: Number(responseValue.herbaceous_lfi_fuel_moisture),
            windSpeed: Number(responseValue.wind_speed),
            kbdi: Number(responseValue.kbdi),
            fuelTemp: Number(responseValue.fuel_temperature),
            gsi: Number(responseValue.gsi),
            temperature: Number(responseValue.temperature),
            sol_rad: Number(responseValue.sol_radiation),
            relative_humidity: Number(responseValue.relative_humidity),
            precip: Number(responseValue.hourly_precipitation),
            snow_flag: (responseValue.snow_flag),
            station_id: stationId.toString(),
          })
        }
        else {
          setTime(null)
          setInitVals({
            calcType: 'Scenario',
            fuelModel: value.fuelModel,
            oneHr: 3,
            tenHr: 4,
            hundredHr: 5,
            thousandHr: 6,
            woodyFM: 60,
            herbFM: 30,
            windSpeed: 5,
            kbdi: 100,
            fuelTemp: 80,
            gsi: 0.3,
            temperature: null,
            sol_rad: null,
            relative_humidity: null,
            precip: null,
            snow_flag: 'N',
            station_id: stationId.toString(),
          });
          setOutputFromCalc({ bi: null, erc: null, sc: null, ic: null })
          addAlert({
            id: '',
            message: alertMessages.calcWarning,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          })
        }
        setIsLoading(false);

      }
      catch (error) {
        console.log('error', error);
        setTime(null)
        setInitVals({
          calcType: 'Scenario',
          fuelModel: value.fuelModel,
          oneHr: 3,
          tenHr: 4,
          hundredHr: 5,
          thousandHr: 6,
          woodyFM: 60,
          herbFM: 30,
          windSpeed: 5,
          kbdi: 100,
          fuelTemp: 80,
          gsi: 0.3,
          temperature: null,
          sol_rad: null,
          relative_humidity: null,
          precip: null,
          snow_flag: 'N',
          station_id: stationId.toString(),
        })

        setIsLoading(false);
        setOutputFromCalc({ bi: null, erc: null, sc: null, ic: null })
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        })
      }
    })();
  }, [addAlert, setInitVals, setIsLoading]);

  const getCatalogName = useCallback((stationId: any) => {
    (async () => {
      try {
        const varRequest = {
          stationId: stationId.toString()
        };
        const response: any = await getNfdrCatalog(varRequest, addAlert);
        if (response) {
          setCatalogLinkedToStation(response[0]);
        }
      }
      catch (error) {
        console.log('error', error)
        setCatalogLinkedToStation(null)
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        })
      }
    })();
  }, [addAlert]);

  const goToCatalog = (station_id: any) => {
    const url = `${process.env.REACT_APP_GRAPHQL_URL}`.split('api/climatology/graphql', 1)[0]
    const newTab = window.open(`${url}tables/catalog/${station_id}`, '_blank')
    if (newTab) {
      window.focus();
    }
  }

  const stationCallback = useCallback(() => {
    (async () => {
      try {
        const varRequest = {
          returnAll: true,
        };
        const response: any = await getStations(varRequest, addAlert);
        if (response) {
          const stations = response.stations.sort(function (a: any, b: any) {
            return a.search_name.localeCompare(b.search_name);
          });
          setStationsOptions(stations);          
          setFailureCount(0)

        }else {
          setStationsOptions([]);
          setFailureCount(failureCount+1)
        }
        
      }
      catch (error) {
        setStationsOptions([])
        console.log('error', error)
        setFailureCount(failureCount+1)
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        })
      }
    })();
  }, [addAlert, setFailureCount, failureCount]);

  useEffect(() => {
    if (stationsOptions?.length<1) {
      stationCallback();
    }
    if(failureCount>0 && failureCount<4){
      stationCallback();
    }
  }, [stationCallback, stationsOptions, failureCount]);

  //gets the output of calculator
  const handleSubmit = (values: any) => {
    setIsLoading(true);
    (async () => {
      try {
        const paramstoSend = {
          stationId: values.station_id === null ? null : Number(values.station_id),
          windSpeed: values.windSpeed === null ? null : Number(values.windSpeed),
          temperature: values.temperature === null ? null : Number(values.temperature),
          solarRadiation: values.sol_rad === null ? null : Number(values.sol_rad),
          relativeHumidity: values.relative_humidity === null ? null : Number(values.relative_humidity),
          precipitation: values.precip === null ? null : Number(values.precip),
          snowFlag: values.snow_flag === null ? null : values.snow_flag?.toString(),
          fuelModel: values.fuelModel
        };
        const responseFromUpdate: any = await getCalcResultsCall(paramstoSend, addAlert);
        if (responseFromUpdate.data?.indices) {
          setOutputFromCalc(responseFromUpdate.data.indices)
          const intermediates = responseFromUpdate.data.intermediateValues;
          setInitVals({
            calcType: 'Scenario',
            fuelModel: values.fuelModel,
            oneHr: truncateTwoDecimals(Number(intermediates.hr1FM)),
            tenHr: truncateTwoDecimals(Number(intermediates.hr10FM)),
            hundredHr: truncateTwoDecimals(Number(intermediates.hr100FM)),
            thousandHr: truncateTwoDecimals(Number(intermediates.hr1000FM)),
            woodyFM: truncateTwoDecimals(Number(intermediates.woodyFM)),
            herbFM: truncateTwoDecimals(Number(intermediates.herbFM)),
            windSpeed: Number(values.windSpeed),
            kbdi: Number(intermediates.kbdi),
            fuelTemp: truncateTwoDecimals(Number(intermediates.fuelTemperature)),
            gsi: Number(intermediates.gsi),
            temperature: Number(values.temperature),
            sol_rad: Number(values.sol_rad),
            relative_humidity: Number(values.relative_humidity),
            precip: Number(values.precip),
            snow_flag: (values.snow_flag),
            station_id: values.station_id.toString(),
          })
        }
        else {
          console.log('error')
          setInitVals({
            calcType: 'Scenario',
            fuelModel: values.fuelModel,
            oneHr: 3,
            tenHr: 4,
            hundredHr: 5,
            thousandHr: 6,
            woodyFM: 60,
            herbFM: 30,
            windSpeed: 5,
            kbdi: 100,
            fuelTemp: 80,
            gsi: 0.3,
            temperature: null,
            sol_rad: null,
            relative_humidity: null,
            precip: null,
            snow_flag: 'N',
            station_id: values.station_id?.toString(),
          })
          setOutputFromCalc({ bi: null, erc: null, sc: null, ic: null })
          setIsLoading(false)
          errorResponse(responseFromUpdate, addAlert, alertMessages.calcResultError);
        }
        setIsLoading(false);
      }
      catch (error: any) {
        setIsLoading(false);
        setInitVals({
          calcType: 'Scenario',
          fuelModel: values.fuelModel,
          oneHr: 3,
          tenHr: 4,
          hundredHr: 5,
          thousandHr: 6,
          woodyFM: 60,
          herbFM: 30,
          windSpeed: 5,
          kbdi: 100,
          fuelTemp: 80,
          gsi: 0.3,
          temperature: null,
          sol_rad: null,
          relative_humidity: null,
          precip: null,
          snow_flag: 'N',
          station_id: values.station_id,
        })
        setOutputFromCalc({ bi: null, erc: null, sc: null, ic: null })
        console.log('error', error);
        errorResponse(error, addAlert, alertMessages.calcResultError);
      }
    })();
  }

  const eventControl = (event) => {
    if (event.type === "mousemove" || event.type === "touchmove") {
      setIsDragging(true);
    }
    if (event.type === "mouseup" || event.type === "touchend") {
      setTimeout(() => {
        setIsDragging(false);
      }, 50);
    }
  };

  return (
    <Draggable bounds="parent" onDrag={eventControl} onStop={eventControl} >
      <Box sx={{
        flexGrow: 1, resize: 'both', position: 'absolute', overflow: 'auto',
        cursor: 'grab', zIndex: 1200, backgroundColor: '#f6f6f6', border: '1px solid #fff',
        borderRadius: '5px', minWidth: '25rem',
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'

      }}>
        <Formik
          initialValues={initVals}
          validateOnBlur={true}
          validateOnChange={true}
          validateOnMount={true}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={(values: any) => { handleSubmit(values) }}>
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            isValid,
            setFieldTouched,
            handleBlur
          }) => {
            const setValue = (event: any, type: string) => {
              setFieldTouched(type, true, false);
              if (event.target.value.length === 1 && /^\s/.test(event.target.value)) {
                return
              }
              if (type === 'fuelModel') {
                setIsLoading(true);
                setFieldValue(type, event.target.value);
                let valWithNewThing = {
                  ...values,
                  fuelModel: event.target.value
                }
                getNFDRSfromWxCallback(Number(values.station_id), valWithNewThing)
              }
              if (type === 'calcType' || type === 'snow_flag') {
                setFieldValue(type, event.target.value);
              }
              if (regex2decimals.test((event.target.value).toString())) {
                setFieldValue(type, event.target.value);

              }
            }

            const handleSearch = (e: React.SyntheticEvent<Element, Event>, val: any) => {
              setFieldTouched('station_id', true, false);
              if (val !== null && typeof val !== 'undefined') {
                const tempAllStations = stationsOptions
                const findValue:any = tempAllStations?.filter((item: any) => (item.search_name === val.search_name) || (item.station_id === val.station_id));              
                if (findValue.length >= 1) {
                  setTz(findValue[0].time_zone)
                  setIsLoading(true)
                  getCatalogName(findValue[0].station_id);
                  getNFDRSfromWxCallback(Number(findValue[0].station_id), values)
                }
              }
              else {
                setTz('')
                setIsLoading(false);
                setFieldValue('station_id', null)
                setCatalogLinkedToStation(null);
                setFieldValue('oneHr', 3)
                setFieldValue('tenHr', 4)
                setFieldValue('hundredHr', 5)
                setFieldValue('thousandHr', 6)
                setFieldValue('woodyFM', 60)
                setFieldValue('herbFM', 30)
                setFieldValue('kbdi', 100)
                setFieldValue('gsi', 0.3)
                setFieldValue('fuelTemp', 80)

                setFieldValue('windSpeed', 5)
                setFieldValue('temperature', null)
                setFieldValue('sol_rad', null)
                setFieldValue('relative_humidity', null)
                setFieldValue('precip', null)
                setFieldValue('snow_flag', 'N')

              }
            }

            return (
              <Form onSubmit={handleSubmit}>
                <Grid container spacing={{ xs: 1, md: 2 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                  <Grid item xs={2} sm={6} md={9} >
                    <Item>
                      <label id={'calcType'} style={{ fontWeight: 700, fontSize: '18px' }}>Calculator Type
                        <Select
                          size='small'
                          value={values.calcType}
                          id='calcType'
                          labelId={'calcType'}
                          style={{ backgroundColor: 'white', marginLeft: '5px', width: '10rem' }}
                          onChange={(event) => { setValue(event, 'calcType') }}
                          MenuProps={MenuProps}
                          label={'Calculator Type'}

                        >
                          <MenuItem key={'Scenario'} value={'Scenario'}>{'Scenario'}</MenuItem>

                        </Select>
                      </label>
                    </Item>
                  </Grid>
                  <Grid item xs={1} sm={1} md={2} />
                  <Grid item xs={1} sm={1} md={1} >
                    <IconButton aria-label='close' style={{ cursor: 'pointer', display: 'float', justifyContent: 'flex-end' }} onClick={() => { setOpenCalc(false) }}>
                      <CloseIcon />

                    </IconButton>
                  </Grid>

                </Grid>
                {
                  values.calcType === 'Scenario' &&
                  <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>

                    <Grid item xs={2} sm={6} md={8}>
                      <Item>
                        <label id={'station'} style={{ display: 'flex', marginLeft: '5px', fontWeight: 700, fontSize: '18px' }}>Station*
                          <div className="searchStationsCalculator">
                            {/* Search Icons */}
                            <i className="fas fa-search" />
                            <Autocomplete
                              id="station"
                              aria-labelledby='station'
                              noOptionsText={
                                <div className='no-results'>
                                  <div>No results match your search criteria.</div>
                                  <div><b>Here are a few tips:</b></div>
                                  <div>• Remember to check your spelling<br />
                                    • Ensure you are searching either a Station Name or Station ID number</div>
                                </div>
                              }
                              onChange={(e, value) => handleSearch(e, value)}
                              options={stationsOptions}
                              getOptionLabel={(option: any) => `${option?.search_name}`}
                              filterOptions={(options, { inputValue }) => {
                                const search = inputValue?.toLowerCase();
                                if (search?.length <= 2) {
                                  return options?.filter((option) =>
                                    option.state?.toLowerCase().includes(search) ||
                                    option.station_id?.toString().includes(search)
                                  )
                                }
                                return options?.filter((option) =>
                                  option.station_name?.toLowerCase().includes(search) ||
                                  option.network_name?.toLowerCase().includes(search) ||
                                  option.state?.toLowerCase().includes(search) ||
                                  option.station_id?.toString().includes(search)
                                )
                              }}
                              aria-label="stations"

                              renderInput={(params) => (
                                <TextField
                                  error={!!errors.station_id && !!touched.station_id}
                                  helperText={errors.station_id && touched.station_id ? errors.station_id : ''}
                                  data-testid={'search-bar'}
                                  aria-label={'Search Station...'}
                                  {...params}
                                  label="Search Station..."
                                  InputProps={{
                                    ...params.InputProps,
                                    type: 'search',
                                  }}
                                />
                              )}
                            />
                          </div>
                        </label>
                      </Item>
                    </Grid>
                    <Grid item xs={2} sm={2} md={4}>
                      <Item>
                        {
                          values.station_id ?(
                            <div>
                              <span>Catalog: </span>
                               <Button variant='text' id='catalog' style={{ fontSize: '17px' }} onClick={() => { goToCatalog(values.station_id) }}>{catalogLinkedToStation?.parent_catalog_name}</Button>
                            </div>
                          )
                            :
                            <span>Please select a station</span>
                        }

                      </Item>
                    </Grid>
                  </Grid>

                }
                <Grid container spacing={{ xs: 1, md: 2 }} columns={{ xs: 4, sm: 8, md: 12 }}>

                  <Grid item xs={2} sm={3} md={4}>
                    <Item>
                      <label id={'CreateUpdatefuelModel'} style={{ marginLeft: '5px', fontWeight: 700, fontSize: '14px' }}>Fuel Model*
                        <Select
                          size='small'
                          style={{ backgroundColor: 'white', marginLeft: '5px' }}
                          value={values.fuelModel}
                          onChange={(e: any) => { setValue(e, 'fuelModel') }}
                          MenuProps={MenuProps}
                          label={'Fuel Model'}
                          id='CreateUpdatefuelModel'
                          labelId='CreateUpdatefuelModel'
                          error={!!errors.fuelModel && !!touched.fuelModel}

                        >
                          {FuelModels.map((fm) => (
                            fm !== 'All' &&
                            <MenuItem
                              key={fm}
                              value={fm}
                            >
                              {fm}
                            </MenuItem>
                          ))}
                        </Select>
                      </label>
                    </Item>
                  </Grid>
                  {values.calcType === 'Basic' &&
                    <Grid item xs={2} sm={4} md={5}>
                      <Item>
                        <span style={{ fontSize: '14px' }}>National Catalog Defaults are being used*</span>
                      </Item>
                    </Grid>
                  }
                  {values.calcType === 'Scenario' &&
                    <Grid item xs={1} sm={2} md={4} justifyContent={'center'} alignItems={'center'}>
                      {time &&
                        <Item style={{justifyContent:'center', alignItems: 'center'}}>
                          <span>Observation Time: {time}{` ${tz}`}</span>
                        </Item>
                      }
                    </Grid>
                  }
                  {values.calcType === 'Scenario' &&
                    <Grid item xs={0} sm={0} md={1} justifyContent={'center'} alignItems={'center'}>

                    </Grid>
                  }
                  <Grid item xs={1} sm={2} md={3}>
                    <Button variant="contained" type="submit" disabled={!isValid}>
                      Calculate NFDRS
                    </Button>
                  </Grid>
                </Grid>

                {
                  values.calcType === 'Scenario' &&
                  <div>
                    <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                      <Grid item xs={2} sm={4} md={4}>
                        <Box component={Paper} sx={{ p: 1, m: 1 }}>
                          <div style={{ margin: '4px' }}><span style={{ fontWeight: 700, fontSize: '16px' }}>WEATHER INPUT</span></div>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label htmlFor={'CreateUpdatetemperature'}>Temperature*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.temperature===null? '': values.temperature}
                                onBlur={handleBlur}
                                error={!!errors.temperature && !!touched.temperature}
                                helperText={errors.temperature && touched.temperature ? errors.temperature : ''}
                                id={'CreateUpdatetemperature'}
                                type={'string'}
                                size='small'
                                fullWidth
                                disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                aria-disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                onChange={(e: any) => { setValue(e, 'temperature') }}
                                placeholder={'Enter Temperature'}
                                InputProps={{
                                  inputProps: {
                                    min: -74,
                                    max: 138
                                  }
                                }}
                              />
                            </Grid>

                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdatesol_rad'} htmlFor={'CreateUpdatesol_rad'}>Solar Radiation*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.sol_rad===null? '': values.sol_rad}
                                onBlur={handleBlur}
                                error={!!errors.sol_rad && !!touched.sol_rad}
                                helperText={errors.sol_rad && touched.sol_rad ? errors.sol_rad : ''}
                                id={'CreateUpdatesol_rad'}
                                type={'string'}
                                size='small'
                                disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'sol_rad') }}
                                placeholder={'Enter Solar Radiation'}
                                aria-disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 1999
                                  }
                                }}
                              />
                            </Grid>

                          </Grid>

                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'relative_humidity'} htmlFor={'relative_humidity'} style={{ display: 'flex' }}>Relative Humidity*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.relative_humidity===null? '' : values.relative_humidity}
                                onBlur={handleBlur}
                                error={!!errors.relative_humidity && !!touched.relative_humidity}
                                helperText={errors.relative_humidity && touched.relative_humidity ? errors.relative_humidity : ''}
                                id={'relative_humidity'}
                                type={'string'}
                                disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                aria-disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'relative_humidity') }}
                                placeholder={'Enter Relative Humidity'}

                                InputProps={{
                                  inputProps: {
                                    min: 1,
                                    max: 99
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'precip'} htmlFor={'precip'}>Precipitation*</label>
                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.precip===null? '': values.precip}
                                onBlur={handleBlur}
                                error={!!errors.precip && !!touched.precip}
                                helperText={errors.precip && touched.precip ? errors.precip : ''}
                                id={'precip'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'precip') }}
                                placeholder={'Enter Precipitation'}
                                disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                aria-disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 20
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdatewindSpeed'} htmlFor={'CreateUpdatewindSpeed'}>Wind Speed*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.windSpeed===null? '': values.windSpeed}
                                onBlur={handleBlur}
                                error={!!errors.windSpeed && !!touched.windSpeed}
                                helperText={errors.windSpeed && touched.windSpeed ? errors.windSpeed : ''}
                                id={'CreateUpdatewindSpeed'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'windSpeed') }}
                                placeholder={"Wind Speed"}
                                disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                aria-disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 99
                                  }
                                }}
                              />
                            </Grid>

                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={4} sm={8} md={12}>
                              <label id={'snow_flag'} >Snow Flag*
                                <Select
                                  size='small'
                                  onBlur={handleBlur}
                                  value={values.snow_flag===null? '': values.snow_flag}
                                  style={{ marginLeft: '3px' }}
                                  id='snow_flag'
                                  onChange={(event) => { setValue(event, 'snow_flag') }}
                                  MenuProps={MenuProps}
                                  label={'Snow Flag'}
                                  disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                  aria-disabled={values.calcType === 'Scenario' && (!values.station_id || !values.fuelModel || !time)}
                                >
                                  <MenuItem key={'No'} value={'N'}>{'N'}</MenuItem>
                                  <MenuItem key={'Yes'} value={'Y'}>{'Y'}</MenuItem>

                                </Select>
                              </label>
                            </Grid>
                          </Grid>

                        </Box>
                      </Grid>
                      <Grid item xs={2} sm={4} md={4}>
                        {isLoading ? (
                          <Box component={Paper} sx={{ p: 1, m: 1 }} style={{ width: '16rem', height: '100%' }}>
                            <LoadingSpinner />
                          </Box>
                        ) : (
                          <Box component={Paper} sx={{ p: 1, m: 1 }}>
                            <div style={{ margin: '4px' }}><span style={{ fontWeight: 700, fontSize: '16px' }}>FUEL MOISTURE </span></div>
                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={5}>
                                <label id={'CreateUpdateoneHr'} htmlFor={'CreateUpdateoneHr'}>1-HR FM*</label>

                              </Grid>
                              <Grid item xs={2} sm={4} md={7}>
                                <TextField
                                  value={values.oneHr===null? '': values.oneHr}
                                  onBlur={handleBlur}
                                  error={!!errors.oneHr && !!touched.oneHr}
                                  helperText={errors.oneHr && touched.oneHr ? errors.oneHr : ''}
                                  id={'CreateUpdateoneHr'}
                                  type={'string'}
                                  size='small'
                                  disabled
                                  aria-disabled
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'oneHr') }}
                                  placeholder={'Enter 1-HR FM'}
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                      max: 40
                                    }
                                  }}
                                />
                              </Grid>

                            </Grid>
                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={5}>
                                <label id={'CreateUpdatetenHr'} htmlFor={'CreateUpdatetenHr'}>10-HR FM*</label>

                              </Grid>
                              <Grid item xs={2} sm={4} md={7}>
                                <TextField
                                  value={values.tenHr===null? '': values.tenHr}
                                  onBlur={handleBlur}
                                  error={!!errors.tenHr && !!touched.tenHr}
                                  helperText={errors.tenHr && touched.tenHr ? errors.tenHr : ''}
                                  id={'CreateUpdatetenHr'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'tenHr') }}
                                  placeholder={'Enter 10-HR FM'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                      max: 40
                                    }
                                  }}
                                />
                              </Grid>

                            </Grid>

                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={5}>
                                <label id={'CreateUpdatehundredHr'} htmlFor={'CreateUpdatehundredHr'} style={{ display: 'flex' }}>100-HR FM*</label>

                              </Grid>
                              <Grid item xs={2} sm={4} md={7}>
                                <TextField
                                  value={values.hundredHr===null? '':values.hundredHr}
                                  onBlur={handleBlur}
                                  error={!!errors.hundredHr && !!touched.hundredHr}
                                  helperText={errors.hundredHr && touched.hundredHr ? errors.hundredHr : ''}
                                  id={'CreateUpdatehundredHr'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'hundredHr') }}
                                  placeholder={'Enter 100-HR FM'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                      max: 40
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={5}>
                                <label id={'CreateUpdatethousandHr'} htmlFor={'CreateUpdatethousandHr'}>1000-HR FM*</label>
                              </Grid>
                              <Grid item xs={2} sm={4} md={7}>
                                <TextField
                                  value={values.thousandHr===null? '': values.thousandHr}
                                  onBlur={handleBlur}
                                  error={!!errors.thousandHr && !!touched.thousandHr}
                                  helperText={errors.thousandHr && touched.thousandHr ? errors.thousandHr : ''}
                                  id={'CreateUpdatethousandHr'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'thousandHr') }}
                                  placeholder={'Enter 1000-HR FM'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                      max: 10
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={5}>
                                <label id={'CreateUpdatewoodyFM'} htmlFor={'CreateUpdatewoodyFM'}>Woody FM*</label>
                              </Grid>
                              <Grid item xs={2} sm={4} md={7}>
                                <TextField
                                  value={values.woodyFM===null? '': values.woodyFM}
                                  onBlur={handleBlur}
                                  error={!!errors.woodyFM && !!touched.woodyFM}
                                  helperText={errors.woodyFM && touched.woodyFM ? errors.woodyFM : ''}
                                  id={'CreateUpdatewoodyFM'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'woodyFM') }}
                                  placeholder={'Enter Woody FM'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 60,
                                      max: 300
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={5}>
                                <label id={'CreateUpdateherbFM'} htmlFor={'CreateUpdateherbFM'}>Herb FM*</label>
                              </Grid>
                              <Grid item xs={2} sm={4} md={7}>
                                <TextField
                                  value={values.herbFM===null? '': values.herbFM}
                                  onBlur={handleBlur}
                                  error={!!errors.herbFM && !!touched.herbFM}
                                  helperText={errors.herbFM && touched.herbFM ? errors.herbFM : ''}
                                  id={'CreateUpdateherbFM'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'herbFM') }}
                                  placeholder={'Enter Herb FM'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 30,
                                      max: 300
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={2} sm={4} md={4}>
                        {isLoading ? (
                          <Box component={Paper} sx={{ p: 1, m: 1 }} style={{ width: '16rem', height: '100%' }}>
                            <LoadingSpinner />
                          </Box>
                        ) : (
                          <Box component={Paper} sx={{ p: 1, m: 1 }}>

                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={4}>
                                <label id={'CreateUpdatekbdi'} htmlFor={'CreateUpdatekbdi'}>KBDI*</label>

                              </Grid>
                              <Grid item xs={2} sm={4} md={8}>
                                <TextField
                                  value={values.kbdi===null? '': values.kbdi}
                                  onBlur={handleBlur}
                                  error={!!errors.kbdi && !!touched.kbdi}
                                  helperText={errors.kbdi && touched.kbdi ? errors.kbdi : ''}
                                  id={'CreateUpdatekbdi'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'kbdi') }}
                                  placeholder={'Enter KBDI'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                      max: 800
                                    }
                                  }}
                                />
                              </Grid>

                            </Grid>

                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={5}>
                                <label id={'CreateUpdateFuelTemp'} htmlFor={'CreateUpdateFuelTemp'}>Fuel Temp*</label>

                              </Grid>
                              <Grid item xs={2} sm={4} md={7}>
                                <TextField
                                  value={values.fuelTemp ===null? '':values.fuelTemp}
                                  onBlur={handleBlur}
                                  error={!!errors.fuelTemp && !!touched.fuelTemp}
                                  helperText={errors.fuelTemp && touched.fuelTemp ? errors.fuelTemp : ''}
                                  id={'CreateUpdateFuelTemp'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'fuelTemp') }}
                                  placeholder={'Enter Fuel Temp'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                      max: 150
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                            <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                              <Grid item xs={2} sm={4} md={4}>
                                <label id={'CreateUpdategsi'} htmlFor={'CreateUpdategsi'}>GSI*</label>
                              </Grid>
                              <Grid item xs={2} sm={4} md={8}>
                                <TextField
                                  value={values.gsi===null? '':values.gsi}
                                  onBlur={handleBlur}
                                  error={!!errors.gsi && !!touched.gsi}
                                  helperText={errors.gsi && touched.gsi ? errors.gsi : ''}
                                  id={'CreateUpdategsi'}
                                  type={'string'}
                                  size='small'
                                  fullWidth
                                  onChange={(e: any) => { setValue(e, 'gsi') }}
                                  placeholder={'Enter GSI'}
                                  disabled
                                  aria-disabled
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                      max: 1
                                    }
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        {isLoading ? (
                          <Box component={Paper} sx={{ p: 1, m: 1 }} style={{ width: '16rem', height: '100%' }}>
                            <LoadingSpinner />
                          </Box>
                        ) : (
                          <Box component={Paper} sx={{ p: 1, m: 1 }}>
                            <div style={{ margin: '4px' }}><span style={{ fontWeight: 700, fontSize: '20px' }}>OUTPUT</span></div>

                            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>

                              <Grid item xs={2} sm={4} md={4}>
                                BI

                              </Grid>
                              <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                                {outputFromCalc?.bi}
                              </Grid>

                            </Grid>
                            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                              <Grid item xs={2} sm={4} md={4}>
                                ERC

                              </Grid>
                              <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                                {outputFromCalc?.erc}
                              </Grid>

                            </Grid>

                            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                              <Grid item xs={2} sm={4} md={4}>
                                SC

                              </Grid>
                              <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                                {outputFromCalc?.sc}
                              </Grid>
                            </Grid>
                            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                              <Grid item xs={2} sm={4} md={4}>
                                IC
                              </Grid>
                              <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                                {outputFromCalc?.ic}
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Grid>
                    </Grid>

                  </div>
                }
                {values.calcType === 'Basic' &&
                  <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                    <Grid item xs={2} sm={4} md={4}>
                      {isLoading ? (
                        <Box component={Paper} sx={{ p: 1, m: 1 }} style={{ width: '16rem', height: '100%' }}>
                          <LoadingSpinner />
                        </Box>
                      ) : (
                        <Box component={Paper} sx={{ p: 1, m: 1 }}>
                          <div style={{ margin: '4px' }}><span style={{ fontWeight: 700, fontSize: '16px' }}>FUEL MOISTURE INPUT</span></div>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            {/*basic calc */}
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdateoneHr'} htmlFor={'CreateUpdateoneHr'}>1-HR FM*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.oneHr===null? '': values.oneHr}
                                onBlur={handleBlur}
                                error={!!errors.oneHr && !!touched.oneHr}
                                helperText={errors.oneHr && touched.oneHr ? errors.oneHr : ''}
                                id={'CreateUpdateoneHr'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'oneHr') }}
                                placeholder={'Enter 1-HR FM'}
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 40
                                  }
                                }}
                              />
                            </Grid>

                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdatetenHr'} htmlFor={'CreateUpdatetenHr'}>10-HR FM*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.tenHr===null? '':values.tenHr}
                                onBlur={handleBlur}
                                error={!!errors.tenHr && !!touched.tenHr}
                                helperText={errors.tenHr && touched.tenHr ? errors.tenHr : ''}
                                id={'CreateUpdatetenHr'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'tenHr') }}
                                placeholder={'Enter 10-HR FM'}

                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 40
                                  }
                                }}
                              />
                            </Grid>

                          </Grid>

                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdatehundredHr'} htmlFor={'CreateUpdatehundredHr'} style={{ display: 'flex' }}>100-HR FM*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.hundredHr===null? '': values.hundredHr}
                                onBlur={handleBlur}
                                error={!!errors.hundredHr && !!touched.hundredHr}
                                helperText={errors.hundredHr && touched.hundredHr ? errors.hundredHr : ''}
                                id={'CreateUpdatehundredHr'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'hundredHr') }}
                                placeholder={'Enter 100-HR FM'}

                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 40
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdatethousandHr'} htmlFor={'CreateUpdatethousandHr'}>1000-HR FM*</label>
                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.thousandHr===null? '':values.thousandHr}
                                onBlur={handleBlur}
                                error={!!errors.thousandHr && !!touched.thousandHr}
                                helperText={errors.thousandHr && touched.thousandHr ? errors.thousandHr : ''}
                                id={'CreateUpdatethousandHr'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'thousandHr') }}
                                placeholder={'Enter 1000-HR FM'}

                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 10
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdatewoodyFM'} htmlFor={'CreateUpdatewoodyFM'}>Woody FM*</label>
                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.woodyFM===null? '': values.woodyFM}
                                onBlur={handleBlur}
                                error={!!errors.woodyFM && !!touched.woodyFM}
                                helperText={errors.woodyFM && touched.woodyFM ? errors.woodyFM : ''}
                                id={'CreateUpdatewoodyFM'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'woodyFM') }}
                                placeholder={'Enter Woody FM'}

                                InputProps={{
                                  inputProps: {
                                    min: 60,
                                    max: 300
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdateherbFM'} htmlFor={'CreateUpdateherbFM'}>Herb FM*</label>
                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.herbFM===null? '': values.herbFM}
                                onBlur={handleBlur}
                                error={!!errors.herbFM && !!touched.herbFM}
                                helperText={errors.herbFM && touched.herbFM ? errors.herbFM : ''}
                                id={'CreateUpdateherbFM'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'herbFM') }}
                                placeholder={'Enter Herb FM'}

                                InputProps={{
                                  inputProps: {
                                    min: 30,
                                    max: 300
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={2} sm={4} md={4}>
                      {isLoading ? (
                        <Box component={Paper} sx={{ p: 1, m: 1 }} style={{ width: '16rem', height: '100%' }}>
                          <LoadingSpinner />
                        </Box>
                      ) : (
                        <Box component={Paper} sx={{ p: 1, m: 1 }}>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={4}>
                              <label id={'CreateUpdatewindSpeed'} htmlFor={'CreateUpdatewindSpeed'}>20' Wind*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={8}>
                              <TextField
                                value={values.windSpeed===null? '': values.windSpeed}
                                onBlur={handleBlur}
                                error={!!errors.windSpeed && !!touched.windSpeed}
                                helperText={errors.windSpeed && touched.windSpeed ? errors.windSpeed : ''}
                                id={'CreateUpdatewindSpeed'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'windSpeed') }}
                                placeholder={"Wind Speed"}

                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 99
                                  }
                                }}
                              />
                            </Grid>

                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={4}>
                              <label id={'CreateUpdatekbdi'} htmlFor={'CreateUpdatekbdi'}>KBDI*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={8}>
                              <TextField
                                value={values.kbdi===null? '':values.kbdi}
                                onBlur={handleBlur}
                                error={!!errors.kbdi && !!touched.kbdi}
                                helperText={errors.kbdi && touched.kbdi ? errors.kbdi : ''}
                                id={'CreateUpdatekbdi'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'kbdi') }}
                                placeholder={'Enter KBDI'}

                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 800
                                  }
                                }}
                              />
                            </Grid>

                          </Grid>

                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={5}>
                              <label id={'CreateUpdateFuelTemp'} htmlFor={'CreateUpdateFuelTemp'}>Fuel Temp*</label>

                            </Grid>
                            <Grid item xs={2} sm={4} md={7}>
                              <TextField
                                value={values.fuelTemp===null? '': values.fuelTemp}
                                onBlur={handleBlur}
                                error={!!errors.fuelTemp && !!touched.fuelTemp}
                                helperText={errors.fuelTemp && touched.fuelTemp ? errors.fuelTemp : ''}
                                id={'CreateUpdateFuelTemp'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'fuelTemp') }}
                                placeholder={'Enter Fuel Temp'}

                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 150
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ paddingBottom: '3px' }}>
                            <Grid item xs={2} sm={4} md={4}>
                              <label id={'CreateUpdategsi'} htmlFor={'CreateUpdategsi'}>GSI*</label>
                            </Grid>
                            <Grid item xs={2} sm={4} md={8}>
                              <TextField
                                value={values.gsi===null? '':values.gsi}
                                onBlur={handleBlur}
                                error={!!errors.gsi && !!touched.gsi}
                                helperText={errors.gsi && touched.gsi ? errors.gsi : ''}
                                id={'CreateUpdategsi'}
                                type={'string'}
                                size='small'
                                fullWidth
                                onChange={(e: any) => { setValue(e, 'gsi') }}
                                placeholder={'Enter GSI'}

                                InputProps={{
                                  inputProps: {
                                    min: 0.01,
                                    max: 1
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={2} sm={4} md={4}>
                      {isLoading ? (
                        <Box component={Paper} sx={{ p: 1, m: 1 }} style={{ width: '16rem', height: '100%' }}>
                          <LoadingSpinner />
                        </Box>
                      ) : (
                        <Box component={Paper} sx={{ p: 1, m: 1 }}>
                          <div style={{ margin: '4px' }}><span style={{ fontWeight: 700, fontSize: '20px' }}>OUTPUT</span></div>

                          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>

                            <Grid item xs={2} sm={4} md={4}>
                              BI

                            </Grid>
                            <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                              {outputFromCalc?.bi}
                            </Grid>

                          </Grid>
                          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                            <Grid item xs={2} sm={4} md={4}>
                              ERC

                            </Grid>
                            <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                              {outputFromCalc?.erc}
                            </Grid>

                          </Grid>

                          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                            <Grid item xs={2} sm={4} md={4}>
                              SC

                            </Grid>
                            <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                              {outputFromCalc?.sc}
                            </Grid>
                          </Grid>
                          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                            <Grid item xs={2} sm={4} md={4}>
                              IC
                            </Grid>
                            <Grid item xs={2} sm={4} md={4} style={{ fontWeight: 700, fontSize: '1rem' }}>
                              {outputFromCalc?.ic}
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Grid>

                  </Grid>
                }
              </Form>
            )
          }}
        </Formik>

      </Box>
    </Draggable>

  )
}